import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap} from "gsap";
import {HOTB} from "../HOTB";
import {AnimUtils} from "../utils/AnimUtils";
import {GuiButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/GuiButton";
import {capItem, HOTBData, HOTBGameData} from "../server/HOTBData";
import {GameSymbol, SymbolsList, SymbolStates} from "../reels/GameSymbol";
import {GameConfig} from "../GameConfig";
import {CustomBounce} from "gsap/dist/CustomBounce";
import {GameUtils} from "../utils/GameUtils";
import {ExtendedGameMode} from "../server/ExtendedGameMode";
import {ExtendedGameModel} from "../server/ExtendedGameModel";
import {AvalancheReelModel} from "../reels/AvalancheReelModel";
import {ReelView} from "../reels/ReelView";
import {GameAssets} from "../GameAssets";
import {GameSounds} from "../sound/GameSounds";

export class PickButton extends GuiButton {
    constructor(name: string, callback?: Function) {
        super(name, callback);

        const tempSprite: PIXI.Sprite = new PIXI.Sprite(PIXI.Texture.WHITE);
        tempSprite.width = 70;
        tempSprite.height = 60;
        tempSprite.anchor.set(0.5);
        tempSprite.alpha = 0;
        this.hitArea = new PIXI.Rectangle((tempSprite.x - (tempSprite.width / 2)), (tempSprite.y - (tempSprite.height / 2)), tempSprite.width, tempSprite.height);

        this.addChild(tempSprite);
    }
}

interface customFontStyle {
    fontName: string;
    fontSize: number;
}

export class PickAndClickView extends PIXI.Container implements StageLayer {
    private _pickCallback: ((index: number) => void) | undefined;
    private _bgSpine: PIXI.spine.Spine;
    private _fgSpine: PIXI.spine.Spine;
    private _button: PickButton;
    private _allCaps: PIXI.spine.Spine[];
    private _capsContainer: PIXI.Container;
    private _maskArea: PIXI.Graphics;
    private _reelAreaMask: PIXI.Graphics;
    private _maxCapCounter: PIXI.BitmapText;
    private _redCapCounter: PIXI.BitmapText;
    private _totalMultiplier: PIXI.BitmapText;
    private _counterFontStyle: customFontStyle = {
        fontName: GameAssets.CAP_COUNTER,
        fontSize: 10
    };
    private _multiDefFontSize: number = 10;
    private _totalMultiFontStyle: customFontStyle = {
        fontName: GameAssets.TOTAL_CAPS_MULTIPLIER,
        fontSize: this._multiDefFontSize
    };

    constructor() {
        super();
        this.name = "PickAndClickView";
        this.init();
    }

    private _totalMultValue: number = 0;

    get totalMultValue(): number {
        return this._totalMultValue;
    }

    set totalMultValue(value: number) {
        this._totalMultValue = value;
    }

    public showView(pickCallback: (index: number) => void, isReplay: boolean): gsap.core.Timeline {
        HOTB.keypadPlugin.setZeroBetSpinCounter(-1);
        this._pickCallback = pickCallback;

        const tl: gsap.core.Timeline = gsap.timeline(),
            gameModel: ExtendedGameModel = HOTB.model,
            hotbData: HOTBData = gameModel.data,
            isFromBonusBuy: boolean = GameUtils.isCapMachineGame() && hotbData.wasFeatureBuy && hotbData.nextMode === ExtendedGameMode.CAPWIN_GAME;

        HOTB.soundPlayer.fadeMusic(0, 0.001);

        if (GameUtils.isSatanSpin()) {
            GameSounds.soundPlayer.fadeTrack({
                name: HOTB.soundPlayer.satanSpinSoundPlaying,
                to: 0,
                duration: 0.001
            });
        }

        tl.add([
            HOTB.gameForeground.showStaticDoors(hotbData.mode, false),
            (): void => {
                GameUtils.toggleSymbols(false);
            }
        ]);

        tl.add((): void => {
            HOTB.soundPlayer.playLoopEffect(GameSounds.CAP_WIN_MACHINE_LOOP);
        });

        if (!(HOTB.model.isRestore) && !isFromBonusBuy) {
            tl.add([
                HOTB.gameForeground.handleDoors(false, false, true),
                HOTB.gameForeground.toggleMachineBanner(true, 0.3)
            ]);

            tl.to(this, {
                duration: 0.75
            });
        }
        if (isFromBonusBuy) {
            tl.add([
                this.playAnimation(this._fgSpine, "flickerEnd")
            ]);
        }

        tl.add((): void => {
            this.createCaps();
        });

        tl.add(this.showCounters());

        if (isFromBonusBuy) {
            tl.add([
                this.playAnimation(this._bgSpine, "win"),
                this.playAnimation(this._fgSpine, "win")
            ]);
            tl.to(this, {
                duration: 1
            });
            tl.add((): void => {
                if (!isReplay) {
                    this.onButtonClick();
                }
            });
        } else {
            tl.add([
                this.playAnimation(this._bgSpine, "idle", true),
                this.playAnimation(this._fgSpine, "idle", true),
                (): void => {
                    if (!(HOTB.model.isRestore) && !isFromBonusBuy) {
                        HOTB.gameForeground.toggleMachineBanner(false, 0.2);
                        HOTB.gameForeground.handleDoors(true, false, true);
                    }
                    if (!isReplay) {
                        this.enablePickButtons(true);
                    }
                }
            ]);
        }

        return tl;
    }

    public hideView(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            gameModel: ExtendedGameModel = HOTB.model,
            capsData: capItem[] = this.getParsedCapsData();
        let isMaxWinCapAwarded: boolean = false,
            delayForMaxWinCap: number = 0.4;

        for (let i: number = 0; i < capsData.length; i++) {
            if (capsData[i].sym === "RED_CAP" && capsData[i].isAwarded) {
                delayForMaxWinCap++;
            }
        }

        if (delayForMaxWinCap > 0.4) {
            delayForMaxWinCap--;
        }

        /*tl.to(this, {
            duration: 0.75
        });*/

        if (capsData.every((item: capItem) => !item.isAwarded)) {
            // if no caps are awarded
            for (let i: number = 0; i < this._allCaps.length; i++) {
                const currCap: PIXI.spine.Spine = this._allCaps[i];
                tl.add(this.playAnimation(currCap, "triggerEnd"));
            }

            tl.add([
                this.updateTotalMultiplier(0),
                this.playAnimation(this._bgSpine, "fail"),
                this.playAnimation(this._fgSpine, "fail"),
                (): void => {
                    HOTB.soundPlayer.stopLoopEffect(GameSounds.CAP_WIN_MACHINE_LOOP_AFTER_BUTTON_PRESS);
                    HOTB.soundPlayer.playSound(GameSounds.CAP_WIN_MACHINE_FAIL);
                    HOTB.soundPlayer.playSound(GameSounds.CAP_WIN_MACHINE_MULTIPLIER_UPDATE);
                }
            ]);
            tl.to(this, {duration: 1.5});
        } else {
            // if cap/caps are awarded
            const nonWinningCaps: PIXI.spine.Spine[] = [];
            let fallDelay: number = 1;

            for (let i: number = 0; i < this._allCaps.length; i++) {
                const currCap: PIXI.spine.Spine = this._allCaps[i],
                    capData: capItem = capsData[i],
                    isMaxWinCap: boolean = capData.sym === "MAGA_CAP";

                if (capData.isAwarded) {
                    if (isMaxWinCap) {
                        isMaxWinCapAwarded = true;
                        tl.add([this.playAnimation(this._fgSpine, "winCap_trigger"),
                            gsap.to(currCap, {duration: 0.4, x: 140, y: 130}),
                            gsap.to(currCap.scale, {duration: 0.4, x: 1.6, y: 1.6}),
                            (): void => {
                                this.playAnimation(currCap, "win");
                                HOTB.soundPlayer.playSound(GameSounds.CAP_WIN_MACHINE_GIVE_CAP_MAX);
                            }
                        ], fallDelay);

                        tl.add(AnimUtils.startVibrate(0.5), fallDelay + 0.4);
                    } else {
                        tl.add([this.playAnimation(currCap, "win"),
                            (): void => {
                                this._capsContainer.addChild(currCap); // to bring the cap up in layer
                                HOTB.soundPlayer.playSound(GameSounds.CAP_WIN_MACHINE_GIVE_CAP);
                            }], fallDelay);

                        tl.add(AnimUtils.playScreenShake(GameConfig.itemDropShakePower), fallDelay + (0.6 + (0.3 - (0.075 * capData.coord.y))));
                    }

                    tl.add((): void => {
                        if (isMaxWinCap) {
                            this.updateMaxCounter();
                            HOTB.soundPlayer.stopLoopEffect(GameSounds.CAP_WIN_MACHINE_LOOP_AFTER_BUTTON_PRESS);
                        } else {
                            this.updateRedCounter();
                        }
                        HOTB.soundPlayer.playSound(GameSounds.CAP_WIN_MACHINE_MULTIPLIER_UPDATE);
                        this.totalMultValue += capData.winTimesBet;
                        this.updateTotalMultiplier(this.totalMultValue);
                    });
                    fallDelay += isMaxWinCap ? delayForMaxWinCap : 1;
                } else {
                    nonWinningCaps.push(currCap);
                }
            }

            if (!(HOTB.model.gameData.brokeTheBank)) {
                fallDelay += 0.5;
            }

            for (let i: number = 0; i < nonWinningCaps.length; i++) {
                const currNoWinCap: PIXI.spine.Spine = nonWinningCaps[i];
                tl.add(this.playAnimation(currNoWinCap, "triggerEnd"), fallDelay);
            }

            tl.add([
                this.playAnimation(this._bgSpine, "win"),
                (): void => HOTB.soundPlayer.stopLoopEffect(GameSounds.CAP_WIN_MACHINE_LOOP_AFTER_BUTTON_PRESS)
            ]);

            if (!isMaxWinCapAwarded) {
                tl.add(this.playAnimation(this._fgSpine, "win"), "<");
            }
        }

        if (!GameUtils.isBigWin() && !gameModel.gameData.brokeTheBank) {
            tl.add((): void => HOTB.soundPlayer.fadeMusic(1, 0.01));
        }

        return tl;
    }

    public resetPickClick(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            gameModel: ExtendedGameModel = HOTB.model,
            hotbData: HOTBData = gameModel.data,
            isCapGameBuy: boolean = hotbData.mode === ExtendedGameMode.CAPWIN_GAME && GameUtils.isCapMachineGame();

        if (isCapGameBuy && !gameModel.gameData.brokeTheBank) {
            tl.add(this.resetCaps(true));
            tl.add([
                this.playAnimation(this._bgSpine, "machineOff"),
                this.playAnimation(this._fgSpine, "machineOff"),
                (): void => {
                    HOTB.soundPlayer.playSound(GameSounds.CAP_WIN_MACHINE_LIGHT_OFF)
                },
            ]);
            tl.add((): void => {
                this._redCapCounter.text = "0";
                this._maxCapCounter.text = "0";
            });
        } else {
            if (!gameModel.gameData.brokeTheBank) {
                tl.add(HOTB.gameForeground.handleDoors(false, false, true));
                tl.add(this.resetCaps(false));
                tl.add(HOTB.gameForeground.handleDoors(true, true));
            }
        }

        tl.add(this.deactivateCapSymbol());

        return tl;
    }

    public onButtonPressed(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
                onStart: (): void => {
                    if (!(HOTB.apiPlugin.isReplay)) {
                        if (this._pickCallback != undefined) {
                            this._pickCallback(1);
                            this._pickCallback = undefined;
                        } else {
                            throw new Error("No pickCallback set");
                        }
                    }
                }
            }),
            gameModel: ExtendedGameModel = HOTB.model,
            hotbData: HOTBData = gameModel.data,
            isCapGameBuy: boolean = GameUtils.isCapMachineGame() && hotbData.wasFeatureBuy && hotbData.nextMode === ExtendedGameMode.CAPWIN_GAME;

        if (isCapGameBuy) {
            tl.add([
                (): void => {
                    HOTB.soundPlayer.playSound(GameSounds.CAP_WIN_MACHINE_PRESS_BUTTON);
                    HOTB.soundPlayer.stopLoopEffect(GameSounds.CAP_WIN_MACHINE_LOOP);
                    HOTB.soundPlayer.playLoopEffect(GameSounds.CAP_WIN_MACHINE_LOOP_AFTER_BUTTON_PRESS);
                    for (let i: number = 0; i < this._allCaps.length; i++) {
                        const currCap: PIXI.spine.Spine = this._allCaps[i];

                        this.playAnimation(currCap, "triggerStart", true);
                    }
                },
                gsap.to(this, {
                    duration: 0.3
                })
            ]);
        } else {
            tl.add([
                this.playAnimation(this._bgSpine, "triggerStart"),
                this.playAnimation(this._fgSpine, "triggerStart"),
                (): void => {
                    HOTB.soundPlayer.playSound(GameSounds.CAP_WIN_MACHINE_PRESS_BUTTON);
                    HOTB.soundPlayer.stopLoopEffect(GameSounds.CAP_WIN_MACHINE_LOOP);
                    HOTB.soundPlayer.playLoopEffect(GameSounds.CAP_WIN_MACHINE_LOOP_AFTER_BUTTON_PRESS);
                    for (let i: number = 0; i < this._allCaps.length; i++) {
                        const currCap: PIXI.spine.Spine = this._allCaps[i];

                        this.playAnimation(currCap, "triggerStart", true);
                    }
                }
            ]);
        }

        return tl;
    }

    public showBBIntro(spinePosY: number = -10): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add([
            this.playAnimation(this._bgSpine, "machineOff"),
            this.playAnimation(this._fgSpine, "machineOff"),
            HOTB.gameForeground.showStaticDoors(HOTB.model.data.mode, false)
        ]);

        tl.fromTo([
            this._bgSpine.position,
            this._fgSpine.position
        ], {
            y: spinePosY
        }, {
            y: GameConfig.CAP_MACHINE_DEF_POS_Y,
            duration: GameConfig.SATAN_SPIN_SYMBOL_DROP_TIME,
            ease: CustomBounce.create("myBounce", {
                strength: 0.2,
                endAtStart: false,
                squash: 1,
                squashID: "myBounce-squash"
            })
        });

        tl.add(this.showCounters());

        return tl;
    }

    public hideBBIntro(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add([
            this.resetCaps(false),
            (): void => {
                HOTB.gameForeground.hideDoors();
            }
        ]);

        return tl;
    }

    public onSpinStart(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add((): void => {
            this.playAnimation(this._bgSpine, "flicker", true);
            this.playAnimation(this._fgSpine, "flicker", true);
            HOTB.soundPlayer.playSound(GameSounds.CAP_WIN_MACHINE_LIGHT_FLICKER_START);
        });

        return tl;
    }

    public resetCaps(showMachine: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add((): void => {
            AnimUtils.resetSpine(this._bgSpine, showMachine);
            AnimUtils.resetSpine(this._fgSpine, showMachine);

            if (this._allCaps) {
                for (let i: number = 0; i < this._allCaps.length; i++) {
                    const currCap: PIXI.spine.Spine = this._allCaps[i];
                    AnimUtils.resetSpine(currCap);
                }
                this._allCaps = [];
            }

            if (this._capsContainer) {
                this._capsContainer.removeChildren();
            }

            if (showMachine) {
                this.showCounters();
            } else {
                this.hideCounters();
            }
        });

        return tl;
    }

    private deactivateCapSymbol(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        for (let reelId: number = 0; reelId < GameConfig.totalReels; reelId++) {
            const currReel: AvalancheReelModel = HOTB.reelArea.mainReelModels[reelId],
                currReelView: ReelView = currReel.reelView;

            for (let symId: number = 0; symId < currReelView.children.length; symId++) {
                const currSym: GameSymbol = <GameSymbol>currReelView.children[symId];

                if (currSym && currSym.symbolName === SymbolsList.MAGA_CAP) {
                    tl.add(currSym.getStateAnimation(SymbolStates.WIN));
                }
            }
        }

        return tl;
    }

    private playAnimation(spineObj: PIXI.spine.Spine, animName: string, isLooped: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(AnimUtils.playSpineAnimation({
            spineObj: spineObj,
            animation: animName,
            playLoop: isLooped
        }));

        return tl;
    }

    private init(): void {
        this.createBackSpine();
        this.createCapsContainer();
        this.createMask();
        this.createFrontSpine();
        this.createMaxCounter();
        this.createRedCounter();
        this.hideCounters();
        this.createTotalMultiplier();
        this.createButton();
        this.createReelAreaMask();
    }

    private createBackSpine(): void {
        this._bgSpine = AnimUtils.createNewSpine({
            path: "pickClickBackgroundSpine",
            name: "Pick&ClickBackground",
            pos: new PIXI.Point(365, GameConfig.CAP_MACHINE_DEF_POS_Y),
            initShow: false
        });

        this.addChild(this._bgSpine);
    }

    private createCapsContainer(): void {
        this._capsContainer = new PIXI.Container();
        this._capsContainer.position.set(170, 300);
        this.addChild(this._capsContainer);
    }

    private createCaps(): void {
        const capsData: capItem[] = this.getParsedCapsData(),
            capWidth: number = 93.5,
            capHeight: number = 93.5;

        this._allCaps = [];

        for (let i: number = 0; i < capsData.length; i++) {
            const currCapData: capItem = capsData[i],
                currCapSpine: PIXI.spine.Spine = AnimUtils.createNewSpine({
                    path: (currCapData.sym === SymbolsList.MAGA_CAP) ? "yelCap" : "redCap",
                    name: "capItem_" + currCapData.coord.x + "_" + currCapData.coord.y,
                    pos: new PIXI.Point((capWidth * currCapData.coord.x), (capHeight * currCapData.coord.y)),
                    initShow: true
                });

            this._allCaps.push(currCapSpine);
            this._capsContainer.addChild(currCapSpine);
        }
    }

    private createMask(): void {
        this._maskArea = new PIXI.Graphics()
            .beginFill(0xFFFF00, 0.5)
            .drawRect(0, 0, 380, 350)
            .endFill();

        this._maskArea.position.set(120, 260);

        this._capsContainer.mask = this._maskArea;
        this.addChild(this._maskArea);
    }

    private createReelAreaMask(): void {
        this._reelAreaMask = new PIXI.Graphics()
            .beginFill(0xFFFF00, 0.5)
            .drawRect(0, 0, 520, 465)
            .endFill();

        this._reelAreaMask.position.set(100, 225);

        this.mask = this._reelAreaMask;
        this.addChild(this._reelAreaMask);
    }

    private createFrontSpine(): void {
        this._fgSpine = AnimUtils.createNewSpine({
            path: "pickClickForegroundSpine",
            name: "Pick&ClickForeground",
            pos: new PIXI.Point(365, GameConfig.CAP_MACHINE_DEF_POS_Y),
            initShow: false
        });

        this.addChild(this._fgSpine);
    }

    private createMaxCounter(): void {
        this._maxCapCounter = new PIXI.BitmapText("0", this._counterFontStyle);
        this._maxCapCounter.anchor = new PIXI.Point(0.5, 0.5);
        this._maxCapCounter.position.set(150, 648);
        this._maxCapCounter.name = "maxCapCounter";
        this.addChild(this._maxCapCounter);
    }

    private createRedCounter(): void {
        this._redCapCounter = new PIXI.BitmapText("0", this._counterFontStyle);
        this._redCapCounter.anchor = new PIXI.Point(0.5, 0.5);
        this._redCapCounter.position.set(203, 648);
        this._redCapCounter.name = "redCapCounter";
        this.addChild(this._redCapCounter);
    }

    private createTotalMultiplier(): void {
        this._totalMultiplier = new PIXI.BitmapText("0x", this._totalMultiFontStyle);
        this._totalMultiplier.anchor = new PIXI.Point(0.5, 0.5);
        this._totalMultiplier.position.set(450, 633);
        this._totalMultiplier.name = "totalMultiplier";
        this._totalMultiplier.visible = false;
        this.addChild(this._totalMultiplier);
    }

    private createButton(): void {
        this._button = new PickButton("Pick&ClickButton", () => this.onButtonClick());
        this._button.enable(false);
        this._button.visible = false;
        this._button.position.set(560, 470);
        this.addChild(this._button);
    }

    private enablePickButtons(enable: boolean): void {
        this._button.enable(enable);
        this._button.visible = true;
    }

    private onButtonClick(): void {
        this.enablePickButtons(false);
        this.onButtonPressed();
    }

    private getParsedCapsData(): capItem[] {
        const hotbData: HOTBData = HOTB.model.data,
            originalCapsData: capItem[] = hotbData.capSymbolList;

        return [
            ...originalCapsData.filter(item => item.sym !== "MAGA_CAP"), // All items except "MAGA_CAP"
            ...originalCapsData.filter(item => item.sym === "MAGA_CAP") // "MAGA_CAP" item moved to the end
        ];
    }

    private updateTotalMultiplier(value: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            gameData: HOTBGameData = HOTB.model.gameData,
            isMaxWin: boolean = gameData.brokeTheBank;

        tl.add((): void => {
            if (isMaxWin && value >= GameConfig.MAX_WIN_MULTIPLIER) {
                value = GameConfig.MAX_WIN_MULTIPLIER;
            }
            this._totalMultiplier.text = value + "x";
        });

        tl.add((): void => {
            if (isMaxWin && value >= GameConfig.MAX_WIN_MULTIPLIER) {
                this._totalMultiplier.fontSize = 6;
            } else {
                this._totalMultiplier.fontSize = this._multiDefFontSize;
            }

            this._totalMultiplier.visible = true;
        });

        return tl;
    }

    private showCounters(dur: number = 0.01): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add((): void => {
            this._redCapCounter.visible = true;
            this._redCapCounter.alpha = 0;
            this._redCapCounter.text = "0";

            this._maxCapCounter.visible = true;
            this._maxCapCounter.alpha = 0;
            this._maxCapCounter.text = "0";

            this.totalMultValue = 0;
            this._totalMultiplier.text = "";
        });

        tl.to([this._redCapCounter, this._maxCapCounter], {
            alpha: 1,
            duration: dur
        });

        return tl;
    }

    private hideCounters(dur: number = 0.01): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add((): void => {
            this._redCapCounter.visible = false;
            this._redCapCounter.text = "0";

            this._maxCapCounter.visible = false;
            this._maxCapCounter.text = "0";

            this.totalMultValue = 0;
            this._totalMultiplier.text = "";
            this._totalMultiplier.visible = false;
        });

        tl.to([this._redCapCounter, this._maxCapCounter], {
            alpha: 0,
            duration: dur
        });

        return tl;
    }

    private updateRedCounter(): void {
        if (this._redCapCounter.text === "") {
            this._redCapCounter.text = "0";
        }

        const currValue: number = parseInt(this._redCapCounter.text);

        this._redCapCounter.text = (currValue + 1) + "";
    }

    private updateMaxCounter(): void {
        if (this._maxCapCounter.text === "") {
            this._maxCapCounter.text = "0";
        }

        const currValue: number = parseInt(this._maxCapCounter.text);

        this._maxCapCounter.text = (currValue + 1) + "";
    }
}